import { EventCoordinates } from "@/src/constants/mock-events.constant";
import { Location } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Platform, Text, View } from "react-native";
import { WebView } from "react-native-webview";
import { SectionCard } from "./event-detail-section-card";

type EventLocationMapProps = Readonly<{
  location: string;
  coordinates?: EventCoordinates;
}>;

const buildMapUrl = (
  location: string,
  coordinates?: EventCoordinates,
): string => {
  const query = coordinates
    ? `${coordinates.lat},${coordinates.lng}`
    : encodeURIComponent(location);
  return `https://maps.google.com/maps?q=${query}&output=embed&z=16`;
};

const EventLocationMap = ({ location, coordinates }: EventLocationMapProps) => {
  const { t } = useTranslation();
  const mapUrl = buildMapUrl(location, coordinates);

  return (
    <SectionCard>
      <Text className="text-base font-semibold mb-1 text-onyx">
        {t("LABEL_LOCATION")}
      </Text>

      <View className="flex-row items-center gap-1.5 mb-3">
        <Location size={13} className="text-onyx" />
        <Text className="text-xs flex-1 text-onyx">{location ?? ""}</Text>
      </View>

      <View className="w-full h-48 rounded-xl overflow-hidden">
        {Platform.OS === "web" ? (
          <iframe
            src={mapUrl}
            className="w-full h-full border-0"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          // Google's embed URL must live inside an <iframe>; loading it
          // directly in a WebView throws "must be used in an iframe", so we
          // wrap it in an iframe via inline HTML on native.
          <WebView
            originWhitelist={["*"]}
            source={{
              html: `<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1.0" /><style>*{margin:0;padding:0;}html,body{height:100%;overflow:hidden;}iframe{border:0;width:100%;height:100%;display:block;}</style></head><body><iframe src="${mapUrl}" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe></body></html>`,
            }}
            style={{ flex: 1 }}
            scrollEnabled={false}
            javaScriptEnabled
            domStorageEnabled
          />
        )}
      </View>
    </SectionCard>
  );
};

export default EventLocationMap;
